import {
  Button,
  Box,
  IconButton,
  Typography,
  Card,
  CardContent,
  Stack,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import { Repayment, Loan } from "../../types/loanTypes";
import CowIcon from "../CowIcon";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmptyState from "./EmptyState";

const RepaymentView: React.FC<{ isMobile: boolean; onBack: () => void }> = ({
  isMobile,
  onBack,
}) => {
  const theme = useTheme();
  const [activeLoan, setActiveLoan] = useState<Loan | null>({
    id: "1",
    cattlePhoto: "ipfs://...",
    amount: 500,
    currency: "AGD",
    interestRate: 10,
    term: 6,
    status: "active",
    nextPayment: "2023-11-15",
    cattleDetails: {
      tagNumber: "BOV-001",
      breed: "Angus",
      age: 3,
      estimatedValue: 800,
    },
  });

  const [repaymentHistory, setRepaymentHistory] = useState<Repayment[]>([
    {
      loanId: "1",
      amount: 100,
      date: "2023-10-15",
      status: "completed",
    },
    {
      loanId: "1",
      amount: 100,
      date: "2023-09-15",
      status: "completed",
    },
  ]);

  const handleRepayment = () => {
    // TODO: Process repayment
    console.log("Processing repayment");
  };

  if (!activeLoan) {
    return (
      <EmptyState
        icon={<CowIcon fill={theme.palette.text.secondary} size={48} />}
        title="No Active Loan"
        description="You don't have any loans that require repayment at this time"
        action={
          <Button variant="contained" onClick={onBack}>
            Back to Loans
          </Button>
        }
      />
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <IconButton onClick={onBack}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ ml: 1 }}>
          Loan Repayment
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ bgcolor: "secondary.main" }}>
              <CowIcon fill="#fff" />
            </Avatar>
            <Box>
              <Typography variant="subtitle1">
                {activeLoan.cattleDetails.breed} #
                {activeLoan.cattleDetails.tagNumber}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {activeLoan.amount} {activeLoan.currency} •{" "}
                {activeLoan.interestRate}% APR
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack spacing={2}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography>Next Payment:</Typography>
              <Typography fontWeight="medium">
                {activeLoan.nextPayment}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography>Amount Due:</Typography>
              <Typography fontWeight="medium">
                {Math.round(activeLoan.amount / activeLoan.term)}{" "}
                {activeLoan.currency}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography>Remaining Balance:</Typography>
              <Typography fontWeight="medium">
                {activeLoan.amount -
                  repaymentHistory.length *
                    Math.round(activeLoan.amount / activeLoan.term)}{" "}
                {activeLoan.currency}
              </Typography>
            </Box>
          </Stack>

          <Button
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            sx={{ mt: 3 }}
            onClick={handleRepayment}
          >
            Make Payment
          </Button>
        </CardContent>
      </Card>

      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        Payment History
      </Typography>

      {repaymentHistory.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center", py: 3 }}
        >
          No repayment history yet
        </Typography>
      ) : (
        <List sx={{ width: "100%", bgcolor: "background.paper" }}>
          {repaymentHistory.map((payment, index) => (
            <ListItem key={index} divider>
              <ListItemAvatar>
                <Avatar
                  sx={{
                    bgcolor:
                      payment.status === "completed"
                        ? "success.main"
                        : "warning.main",
                  }}
                >
                  {payment.status === "completed" ? "✓" : "!"}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={`${payment.amount} ${activeLoan.currency}`}
                secondary={`Paid on ${payment.date}`}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default RepaymentView;
